/**
 * RunsTile — périodes de paie de l'année : liste + création (Batch #14.250).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, ArrowRight, CalendarClock, X, Save, Sparkles, Pencil } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, fmtCAD, FREQ_LABELS, FREQ_DAYS, addDays, today, inputCls, Field, Btn, StatusBadge, tileHeaderCls, thCls, tdCls, err } from "./_shared";
import YearCalendarForm from "./YearCalendarForm";
import RunEditForm from "./RunEditForm";

function nextPeriod(runs, frequency) {
  const last = runs.filter((r) => r.frequency === frequency).sort((a, b) => (a.period_end < b.period_end ? 1 : -1))[0];
  if (frequency === "monthly") {
    const d = last ? new Date(last.period_end + "T00:00:00") : new Date();
    if (last) d.setDate(d.getDate() + 1); else d.setDate(1);
    const start = d.toISOString().slice(0, 10);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    return { period_start: start, period_end: end, pay_date: end };
  }
  const start = last ? addDays(last.period_end, 1) : today();
  const end = addDays(start, FREQ_DAYS[frequency] - 1);
  return { period_start: start, period_end: end, pay_date: addDays(end, 5) };
}

function RunForm({ masterId, runs, defaultFrequency, onDone, onCancel }) {
  const [f, setF] = useState({ frequency: defaultFrequency || "biweekly", note: "", ...nextPeriod(runs, defaultFrequency || "biweekly") });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setFreq = (v) => setF((x) => ({ ...x, frequency: v, ...nextPeriod(runs, v) }));
  const submit = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/payroll/${masterId}/runs`, f, { headers: authH() });
      toast.success(`Période créée — ${r.data.nb_employees} employé(s)`);
      onDone(r.data);
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <div className="bg-white border-2 border-emerald-200 rounded-xl p-4 space-y-3" data-testid="paie-run-form">
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Field label="Fréquence" hint="Employés actifs payés à cette fréquence">
          <select className={inputCls} value={f.frequency} onChange={(e) => setFreq(e.target.value)} data-testid="paie-run-frequency">
            {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Début de période"><input type="date" className={inputCls} value={f.period_start} onChange={(e) => set("period_start", e.target.value)} data-testid="paie-run-start" /></Field>
        <Field label="Fin de période"><input type="date" className={inputCls} value={f.period_end} onChange={(e) => set("period_end", e.target.value)} data-testid="paie-run-end" /></Field>
        <Field label="Date de paie"><input type="date" className={inputCls} value={f.pay_date} onChange={(e) => set("pay_date", e.target.value)} data-testid="paie-run-pay-date" /></Field>
        <Field label="Note"><input className={inputCls} value={f.note} onChange={(e) => set("note", e.target.value)} /></Field>
      </div>
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <Btn tone="slate" icon={X} onClick={onCancel}>Annuler</Btn>
        <Btn tone="emerald" icon={Save} onClick={submit} busy={saving} testId="paie-run-create">Créer la période</Btn>
      </div>
    </div>
  );
}

export default function RunsTile({ masterId, year, settings, onChanged }) {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [calendar, setCalendar] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/payroll/${masterId}/runs?year=${year}`, { headers: authH() });
      setRuns(r.data.items || []);
    } catch (e) { err(e); } finally { setLoading(false); }
  }, [masterId, year]);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Supprimer la période ${r.period_start} → ${r.period_end} ?`)) return;
    try {
      await axios.delete(`${API}/admin/payroll/${masterId}/runs/${r.id}`, { headers: authH() });
      toast.success("Période supprimée"); load(); onChanged?.();
    } catch (e) { err(e); }
  };
  const open = (r) => navigate(`/admin/paie/${masterId}/periodes/${r.id}`);

  return (
    <div className="space-y-4" data-testid="paie-runs">
      <div className={tileHeaderCls}>
        <div className="text-sm text-slate-600 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-emerald-700" /> {runs.length} période(s) en {year}</div>
        <div className="flex flex-col sm:flex-row gap-2">
          {!calendar && <Btn tone="slate" icon={Sparkles} onClick={() => { setCalendar(true); setCreating(false); }} testId="paie-year-calendar-btn" className="w-full sm:w-auto">Générer le calendrier {year}</Btn>}
          {!creating && <Btn tone="emerald" icon={Plus} onClick={() => { setCreating(true); setCalendar(false); }} testId="paie-run-add" className="w-full sm:w-auto">Nouvelle période de paie</Btn>}
        </div>
      </div>
      {calendar && <YearCalendarForm masterId={masterId} year={year} defaultFrequency={settings.default_frequency} onDone={() => { setCalendar(false); load(); onChanged?.(); }} onCancel={() => setCalendar(false)} />}
      {creating && <RunForm masterId={masterId} runs={runs} defaultFrequency={settings.default_frequency} onDone={(r) => { setCreating(false); onChanged?.(); open(r); }} onCancel={() => setCreating(false)} />}
      {editing && <RunEditForm masterId={masterId} run={editing} onDone={() => { setEditing(null); load(); onChanged?.(); }} onCancel={() => setEditing(null)} />}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <p className="p-6 text-sm text-slate-500">Chargement…</p> : runs.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="paie-runs-empty">Aucune période de paie en {year}. Créez la première : les heures par défaut des employés seront pré-remplies.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className={thCls}>Période</th><th className={thCls}>Paie le</th><th className={thCls}>Fréquence</th><th className={`${thCls} text-right`}>Employés</th>
                <th className={`${thCls} text-right`}>Brut</th><th className={`${thCls} text-right`}>Net</th><th className={thCls}>Statut</th><th className={`${thCls} text-right`}>Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {runs.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => open(r)} data-testid={`paie-run-row-${r.id}`}>
                    <td className={`${tdCls} font-semibold text-slate-800`}>{r.period_start} → {r.period_end}
                      <div className="text-[11px] text-slate-400 font-normal flex items-center gap-1.5 flex-wrap">#{r.year}-{String(r.sequence).padStart(3, "0")}{r.note ? ` · ${r.note}` : ""}
                        {r.source === "portal" && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold" data-testid={`paie-run-client-created-${r.id}`}>Créée par le client</span>}
                        {r.client_submitted_at && ["draft", "calculated"].includes(r.status) && <span className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200 text-[10px] font-semibold" data-testid={`paie-run-client-submitted-${r.id}`}>Heures soumises par le client</span>}
                        {r.portal_published_at && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-semibold" data-testid={`paie-run-portal-published-${r.id}`}>Docs au portail</span>}
                        {(r.holidays || []).map((h) => <span key={h.date} className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold" title={`Férié à payer : ${h.label} (${h.date})`} data-testid={`paie-run-holiday-${r.id}-${h.date}`}>Férié {h.date.slice(5)} · {h.label}</span>)}
                      </div></td>
                    <td className={tdCls}>{r.pay_date}{r.pay_date_planned && <div className="text-[10px] text-amber-700" title="Date prévue décalée (férié ou fin de semaine)">décalée du {r.pay_date_planned.slice(5)}</div>}</td>
                    <td className={tdCls}>{FREQ_LABELS[r.frequency]}</td>
                    <td className={`${tdCls} text-right`}>{r.nb_employees}</td>
                    <td className={`${tdCls} text-right font-mono`}>{r.totals ? fmtCAD(r.totals.gross) : "—"}</td>
                    <td className={`${tdCls} text-right font-mono`}>{r.totals ? fmtCAD(r.totals.net) : "—"}</td>
                    <td className={tdCls}><StatusBadge status={r.status} testId={`paie-run-status-${r.id}`} /></td>
                    <td className={`${tdCls} text-right whitespace-nowrap`} onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => open(r)} className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-50" title="Ouvrir" data-testid={`paie-run-open-${r.id}`}><ArrowRight className="w-4 h-4" /></button>
                      {["draft", "calculated"].includes(r.status) && <button onClick={() => { setEditing(r); setCreating(false); setCalendar(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="p-1.5 rounded-lg text-sky-700 hover:bg-sky-50" title="Modifier les dates" data-testid={`paie-run-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>}
                      {["draft", "calculated"].includes(r.status) && <button onClick={() => remove(r)} className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`paie-run-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
